import ButtonWithLoading from '@/components/ButtonWithLoading.comp.tsx';
import { getAppLanguage } from '@/i18n.ts';
import { useUserContext } from '@/utils/providers/userContext/useUserContext.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { TONE_STYLES } from '@/views/admin/candidates/candidateStatus.ts';
import { Segmented } from '@/views/admin/dashboard/DashboardCard.comp.tsx';
import { COLORS, formatNumber } from '@/views/admin/dashboard/dashboardTheme.ts';
import { useGetAdminDashboard } from '@/views/admin/dashboard/useGetAdminDashboard.util.ts';
import {
  isValidRange,
  type PeriodPreset,
  type ReportPeriod,
  toRange,
  WHOLE_RECRUITMENT,
} from '@/views/admin/reports/reportPeriod.ts';
import { useDownloadQuizResultsExport } from '@/views/admin/util/useDownloadQuizResultsExport.util.ts';
import { useFullCandidatesExport } from '@/views/admin/util/useFullCandidatesExport.util.ts';
import React, { ReactNode, useMemo, useState } from 'react';
import { Alert, ProgressBar } from 'react-bootstrap';
import {
  IoCalendarOutline,
  IoDocumentTextOutline,
  IoDownloadOutline,
  IoGridOutline,
  IoPrintOutline,
} from 'react-icons/io5';

const inputStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  border: '1px solid #ccc',
  color: COLORS.ink,
  fontSize: 14,
  height: 36,
  padding: '0 10px',
};

// yyyy-mm-dd as a local day, not as UTC midnight, which is the day before west of Greenwich
const parseDay = (day: string): Date => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
};

interface Props {
  onPrintDashboard: (period: ReportPeriod) => void;
}

export const ReportsTab: React.FC<Props> = ({ onPrintDashboard }) => {
  const { t } = useTranslationWithPrefix('views.admin.reports');
  const { user } = useUserContext();
  const adminUserId = user?.userId ?? '';
  const language = getAppLanguage();
  const [period, setPeriod] = useState<ReportPeriod>(WHOLE_RECRUITMENT);
  // the last N days end on the day the tab was opened
  const [today] = useState(() => new Date());
  const [downloadFailed, setDownloadFailed] = useState(false);
  const { downloadQuizResultsExport, isDownloading } = useDownloadQuizResultsExport();
  const fullExport = useFullCandidatesExport({ adminUserId });

  const range = useMemo(() => toRange(period, today), [period, today]);
  const valid = isValidRange(range);
  const { dashboard } = useGetAdminDashboard({
    adminUserId: valid ? adminUserId : '',
    filter: range,
    refetchInterval: false,
  });

  const day = new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const rangeText =
    period.preset === 'all' || (!range.from && !range.to)
      ? t('period.sinceStart')
      : range.from
        ? day.formatRange(parseDay(range.from), range.to ? parseDay(range.to) : today)
        : `${t('period.sinceStart')} – ${day.format(parseDay(range.to ?? ''))}`;

  const downloadQuizResults = async () => {
    setDownloadFailed(false);
    try {
      await downloadQuizResultsExport({ adminUserId, ...range });
    } catch {
      setDownloadFailed(true);
    }
  };

  const startFullExport = async () => {
    try {
      await fullExport.startExport(range);
    } catch {
      // shown through fullExport.errorMessage
    }
  };

  const isFullExportInProgress =
    fullExport.uiState === 'pending' ||
    fullExport.uiState === 'running' ||
    fullExport.uiState === 'completed';

  const summary = [
    { label: t('summary.candidates'), value: dashboard?.kpis.candidates },
    { label: t('summary.quizzesFinished'), value: dashboard?.kpis.quizzesFinished },
    { label: t('summary.recommended'), value: dashboard?.kpis.recommended },
  ];

  return (
    <div style={{ backgroundColor: '#f4f4f4', margin: '-1.25rem -1.6875rem', minHeight: '100%' }}>
      <div className="flex flex-col gap-5 p-4 lg:p-6">
        <div>
          <h1 className="m-0 text-2xl font-bold" style={{ color: COLORS.ink }}>
            {t('heading')}
          </h1>
          <p className="m-0 mt-1 text-sm" style={{ color: COLORS.inkSecondary }}>
            {t('subtitle')}
          </p>
        </div>

        {downloadFailed && (
          <Alert
            className="m-0"
            dismissible
            onClose={() => setDownloadFailed(false)}
            variant="danger"
          >
            {t('downloadError')}
          </Alert>
        )}

        <section
          aria-label={t('period.label')}
          className="flex flex-col gap-4 bg-white"
          style={{ border: '1px solid #e5e5e5', padding: 20 }}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-bold" style={{ color: COLORS.ink }}>
              {t('period.label')}
            </span>
            <Segmented<PeriodPreset>
              ariaLabel={t('period.label')}
              onChange={preset => setPeriod(current => ({ ...current, preset }))}
              options={[
                { label: t('period.days7'), value: '7' },
                { label: t('period.days30'), value: '30' },
                { label: t('period.days90'), value: '90' },
                { label: t('period.all'), value: 'all' },
                { label: t('period.custom'), value: 'custom' },
              ]}
              value={period.preset}
            />
            {period.preset === 'custom' && (
              <div className="flex flex-wrap items-center gap-3">
                <label
                  className="inline-flex items-center gap-2 text-sm"
                  style={{ color: COLORS.inkSecondary }}
                >
                  {t('period.from')}
                  <input
                    max={period.to || undefined}
                    onChange={event =>
                      setPeriod(current => ({ ...current, from: event.target.value }))
                    }
                    style={inputStyle}
                    type="date"
                    value={period.from}
                  />
                </label>
                <label
                  className="inline-flex items-center gap-2 text-sm"
                  style={{ color: COLORS.inkSecondary }}
                >
                  {t('period.to')}
                  <input
                    min={period.from || undefined}
                    onChange={event =>
                      setPeriod(current => ({ ...current, to: event.target.value }))
                    }
                    style={inputStyle}
                    type="date"
                    value={period.to}
                  />
                </label>
              </div>
            )}
          </div>
          {!valid && (
            <div
              className="text-sm font-bold"
              role="alert"
              style={{ color: TONE_STYLES.critical.color }}
            >
              {t('period.invalid')}
            </div>
          )}

          <div
            className="flex flex-wrap items-end gap-x-10 gap-y-4 pt-4"
            style={{ borderTop: `1px solid ${COLORS.grid}` }}
          >
            <div>
              <div
                className="text-xs font-bold uppercase tracking-wider"
                style={{ color: COLORS.inkMuted }}
              >
                {t('summary.title')}
              </div>
              <div className="mt-1 text-sm font-bold" style={{ color: COLORS.ink }}>
                {rangeText}
              </div>
            </div>
            {summary.map(item => (
              <div key={item.label}>
                <div
                  className="text-3xl leading-none font-bold"
                  style={{
                    color: COLORS.ink,
                    fontVariantNumeric: 'tabular-nums',
                    opacity: valid && item.value !== undefined ? 1 : 0.3,
                  }}
                >
                  {valid && item.value !== undefined ? formatNumber(item.value, language) : '—'}
                </div>
                <div className="mt-1 text-xs" style={{ color: COLORS.inkSecondary }}>
                  {item.label}
                </div>
              </div>
            ))}
            <p className="m-0 max-w-[320px] text-xs" style={{ color: COLORS.inkMuted }}>
              {t('summary.hint')}
            </p>
          </div>
        </section>

        <div className="grid gap-4 lg:grid-cols-3">
          <ReportCard
            contents={t('quizResults.contents')}
            contentsLabel={t('contents')}
            description={t('quizResults.description')}
            format={t('formats.excel')}
            icon={<IoDocumentTextOutline aria-hidden size={22} />}
            scope={t('quizResults.scope')}
            title={t('quizResults.title')}
          >
            <ButtonWithLoading
              className="btn btn-primary"
              disabled={!valid}
              isLoading={isDownloading}
              onClick={() => void downloadQuizResults()}
            >
              {/* the button shows its content as a block, which would put the icon above the text */}
              <span className="inline-flex items-center gap-2">
                <IoDownloadOutline aria-hidden />
                {t('quizResults.button')}
              </span>
            </ButtonWithLoading>
          </ReportCard>

          <ReportCard
            contents={t('fullExport.contents')}
            contentsLabel={t('contents')}
            description={t('fullExport.description')}
            format={t('formats.excel')}
            icon={<IoGridOutline aria-hidden size={22} />}
            scope={t('fullExport.scope')}
            title={t('fullExport.title')}
          >
            {fullExport.uiState === 'failed' && fullExport.errorMessage && (
              <Alert className="py-2" dismissible onClose={fullExport.dismiss} variant="danger">
                {fullExport.errorMessage}
              </Alert>
            )}
            {isFullExportInProgress && (
              <div className="mb-3 w-full">
                <div
                  className="mb-1 flex justify-between text-xs"
                  style={{ color: COLORS.inkSecondary }}
                >
                  <span>
                    {fullExport.uiState === 'completed'
                      ? t('fullExport.downloading')
                      : t('fullExport.generating')}
                  </span>
                  <span>{fullExport.progress}%</span>
                </div>
                <ProgressBar
                  animated={fullExport.uiState !== 'completed'}
                  now={fullExport.uiState === 'completed' ? 100 : fullExport.progress}
                  striped
                />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <ButtonWithLoading
                className="btn btn-primary"
                disabled={!valid || isFullExportInProgress}
                isLoading={fullExport.isStarting}
                onClick={() => void startFullExport()}
              >
                <span className="inline-flex items-center gap-2">
                  <IoDownloadOutline aria-hidden />
                  {fullExport.uiState === 'failed' ? t('fullExport.retry') : t('fullExport.start')}
                </span>
              </ButtonWithLoading>
              {isFullExportInProgress && (
                <button
                  className="btn btn-outline-secondary"
                  onClick={fullExport.dismiss}
                  type="button"
                >
                  {t('fullExport.stopTracking')}
                </button>
              )}
            </div>
          </ReportCard>

          <ReportCard
            contents={t('dashboardPdf.contents')}
            contentsLabel={t('contents')}
            description={t('dashboardPdf.description')}
            format={t('formats.pdf')}
            icon={<IoPrintOutline aria-hidden size={22} />}
            scope={t('dashboardPdf.scope')}
            title={t('dashboardPdf.title')}
          >
            <button
              className="btn btn-primary"
              disabled={!valid}
              onClick={() => onPrintDashboard(period)}
              type="button"
            >
              <IoPrintOutline aria-hidden className="me-2" />
              {t('dashboardPdf.button')}
            </button>
            <p className="m-0 mt-2 text-xs" style={{ color: COLORS.inkMuted }}>
              {t('dashboardPdf.hint')}
            </p>
          </ReportCard>
        </div>
      </div>
    </div>
  );
};

interface ReportCardProps {
  children: ReactNode;
  contents: string;
  contentsLabel: string;
  description: string;
  format: string;
  icon: ReactNode;
  scope: string;
  title: string;
}

const ReportCard: React.FC<ReportCardProps> = ({
  children,
  contents,
  contentsLabel,
  description,
  format,
  icon,
  scope,
  title,
}) => (
  // padding is inline: Boosted's .p-5 is !important and three times as wide
  <article
    className="flex min-w-0 flex-col bg-white"
    style={{ border: '1px solid #e5e5e5', padding: 24 }}
  >
    <div className="flex items-start justify-between gap-3">
      <span
        className="inline-flex h-11 w-11 items-center justify-center"
        style={{ backgroundColor: '#fff1e3', color: '#8a3c00' }}
      >
        {icon}
      </span>
      <span
        className="px-2 py-1 text-xs font-bold uppercase tracking-wider"
        style={{ border: `1px solid ${COLORS.ink}`, color: COLORS.ink }}
      >
        {format}
      </span>
    </div>
    <h2 className="m-0 mt-4 text-lg font-bold" style={{ color: COLORS.ink }}>
      {title}
    </h2>
    <p className="m-0 mt-2 text-sm" style={{ color: COLORS.inkSecondary }}>
      {description}
    </p>
    <div
      className="mt-4 text-xs font-bold uppercase tracking-wider"
      style={{ color: COLORS.inkMuted }}
    >
      {contentsLabel}
    </div>
    <p className="m-0 mt-1 text-sm" style={{ color: COLORS.ink }}>
      {contents}
    </p>
    <div
      className="mt-4 inline-flex items-center gap-2 text-sm font-bold"
      style={{ color: COLORS.ink }}
    >
      <IoCalendarOutline aria-hidden />
      {scope}
    </div>
    <div className="mt-auto pt-6">{children}</div>
  </article>
);
